// StepDescription - Tagline, GoodFactor, Pitch, Description (Schritt 3)
// Mit KI-Unterstützung für die Generierung von GoodFactor und Pitch

import React, { useState } from 'react';
import { Sparkles, Wand2, FileText, Loader2, X, Check, Type, AlignLeft } from 'lucide-react';
import type {
  MainCategory, GoodWorkCategory, MoodLabel, ActivityLabel, CommunityFeature, VibeTag,
  Realm, PlaceAmenities, PlaceWorkspaces, GoodWorkVibeTag
} from '../../../types';
import { generateGoodFactorSuggestion, isAIAvailable, type GoodFactorSuggestion } from '../../../services/geminiService';

interface FormData {
  // Required
  tagline: string;
  // Optional
  goodFactor: string;
  pitch: string;
  description: string;
  // Context for AI
  name?: string;
  category?: MainCategory | '';              // GoodLife-Kategorie
  goodworkCategory?: GoodWorkCategory | '';  // GoodWork-Kategorie
  address?: string;
  city?: string;
  moods?: MoodLabel[];
  activities?: ActivityLabel[];
  community?: CommunityFeature[];
  vibeTags?: VibeTag[];
  // GoodWork-specific fields for AI
  realms?: Realm[];
  amenities?: PlaceAmenities;
  workspaces?: PlaceWorkspaces;
  goodworkVibeTags?: GoodWorkVibeTag[];
}

interface StepDescriptionProps {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
}

export function StepDescription({ formData, updateFormData }: StepDescriptionProps) {
  // AI Generation states
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<GoodFactorSuggestion | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  // AI Generation handlers
  const handleGenerateAI = async () => {
    if (!isAIAvailable()) {
      setAiError('KI-Service nicht verfügbar. Bitte später erneut versuchen.');
      return;
    }

    if (!formData.name) {
      setAiError('Bitte zuerst Name im ersten Schritt ausfüllen.');
      return;
    }

    setIsGeneratingAI(true);
    setAiError(null);
    setAiSuggestion(null);

    try {
      // Determine if this is a GoodWork place
      const isGoodWork = formData.realms?.includes('goodwork');

      const suggestion = await generateGoodFactorSuggestion({
        name: formData.name,
        category: formData.category || undefined,
        tagline: formData.tagline,
        description: formData.description,
        address: formData.address,
        city: formData.city,
        moods: formData.moods || [],
        activities: formData.activities || [],
        community: formData.community || [],
        vibeTags: formData.vibeTags || [],
        // GoodWork-specific fields
        realm: isGoodWork ? 'goodwork' : 'goodlife',
        goodworkCategory: formData.goodworkCategory || undefined,
        goodworkVibeTags: formData.goodworkVibeTags || [],
        amenities: formData.amenities,
        workspaces: formData.workspaces,
      });

      if (suggestion) {
        setAiSuggestion(suggestion);
      } else {
        setAiError('Konnte keinen Vorschlag generieren. Bitte später erneut versuchen.');
      }
    } catch (error) {
      console.error('Error generating AI suggestion:', error);
      setAiError('Fehler bei der KI-Generierung. Bitte später erneut versuchen.');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const applyAISuggestion = (field: 'goodFactor' | 'pitch' | 'both') => {
    if (!aiSuggestion) return;

    if (field === 'goodFactor' || field === 'both') {
      updateFormData({ goodFactor: aiSuggestion.goodFactor });
    }
    if (field === 'pitch' || field === 'both') {
      updateFormData({ pitch: aiSuggestion.pitch });
    }

    if (field === 'both') {
      setAiSuggestion(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Was macht diesen Ort besonders?
        </h2>
        <p className="text-gray-600">
          Beschreibe den GoodPlace und warum er ein guter Ort ist.
        </p>
      </div>

      {/* Tagline - Required */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <span className="flex items-center gap-2">
            <Type className="w-4 h-4 text-indigo-600" />
            Kurzbeschreibung (Tagline) <span className="text-red-500">*</span>
          </span>
        </label>
        <input
          type="text"
          value={formData.tagline || ''}
          onChange={(e) => updateFormData({ tagline: e.target.value })}
          placeholder="z.B. Wo Kaffee auf Kultur trifft"
          maxLength={100}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
        />
        <p className="text-xs text-gray-500 mt-1">
          {(formData.tagline || '').length}/100 Zeichen - Erscheint auf Karten und in der Übersicht
        </p>
      </div>

      {/* AI Generation Banner */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Wand2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">KI-Unterstützung</p>
              <p className="text-sm text-gray-600">
                Lass die KI basierend auf deinen Angaben Texte generieren
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleGenerateAI}
            disabled={isGeneratingAI || !formData.name}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isGeneratingAI ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generiert...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                KI-Text generieren
              </>
            )}
          </button>
        </div>

        {!formData.name && (
          <p className="text-xs text-purple-600 mt-2">
            Bitte zuerst den Namen im ersten Schritt ausfüllen.
          </p>
        )}
      </div>

      {/* AI Error */}
      {aiError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {aiError}
        </div>
      )}

      {/* AI Suggestion Preview */}
      {aiSuggestion && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-800">
              <Sparkles className="w-5 h-5" />
              <span className="font-medium">KI-Vorschlag</span>
            </div>
            <button
              type="button"
              onClick={() => setAiSuggestion(null)}
              className="p-1 hover:bg-green-100 rounded transition"
            >
              <X className="w-4 h-4 text-green-600" />
            </button>
          </div>

          {/* GoodFactor Suggestion */}
          <div className="bg-white p-3 rounded-lg border border-green-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Kurzer Highlight-Text</span>
              <button
                type="button"
                onClick={() => applyAISuggestion('goodFactor')}
                className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white text-xs rounded font-medium hover:bg-green-700 transition"
              >
                <Check className="w-3 h-3" />
                Übernehmen
              </button>
            </div>
            <p className="text-gray-600 text-sm italic">"{aiSuggestion.goodFactor}"</p>
            <p className="text-xs text-gray-400 mt-1">{aiSuggestion.goodFactor.length} Zeichen</p>
          </div>

          {/* Pitch Suggestion */}
          <div className="bg-white p-3 rounded-lg border border-green-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Ausführliche Beschreibung</span>
              <button
                type="button"
                onClick={() => applyAISuggestion('pitch')}
                className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white text-xs rounded font-medium hover:bg-green-700 transition"
              >
                <Check className="w-3 h-3" />
                Übernehmen
              </button>
            </div>
            <p className="text-gray-600 text-sm italic line-clamp-3">"{aiSuggestion.pitch}"</p>
            <p className="text-xs text-gray-400 mt-1">{aiSuggestion.pitch.length} Zeichen</p>
          </div>

          {/* Apply Both Button */}
          <button
            type="button"
            onClick={() => applyAISuggestion('both')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition"
          >
            <Check className="w-4 h-4" />
            Beide Texte übernehmen
          </button>
        </div>
      )}

      {/* GoodFactor Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <span className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-green-600" />
            Darum ist das ein GoodPlace
          </span>
        </label>
        <p className="text-sm text-gray-500 mb-2">
          Ein kurzer, prägnanter Satz der das Besondere auf den Punkt bringt (100-150 Zeichen ideal)
        </p>
        <textarea
          value={formData.goodFactor || ''}
          onChange={(e) => updateFormData({ goodFactor: e.target.value })}
          rows={3}
          maxLength={200}
          placeholder="z.B. Hier wird Kultur demokratisiert. Man darf in den Sesseln lümmeln - ohne Konsumzwang."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
        />
        <div className="flex justify-between mt-1">
          <p className="text-xs text-gray-500">
            Erscheint auf der Karten-Ansicht
          </p>
          <p className={`text-xs ${(formData.goodFactor || '').length > 150 ? 'text-amber-600' : 'text-gray-400'}`}>
            {(formData.goodFactor || '').length}/200 Zeichen
          </p>
        </div>
      </div>

      {/* Pitch Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <span className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Ausführlicher Pitch
          </span>
        </label>
        <p className="text-sm text-gray-500 mb-2">
          Erzähle die Geschichte des Ortes. Was kann man hier erleben? (400-600 Zeichen ideal)
        </p>
        <textarea
          value={formData.pitch || ''}
          onChange={(e) => updateFormData({ pitch: e.target.value })}
          rows={6}
          maxLength={800}
          placeholder="z.B. Die Monacensia ist mehr als eine Bibliothek. In der prächtigen Künstlervilla am Friedensengel verschmelzen Literatur und Leben..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
        />
        <div className="flex justify-between mt-1">
          <p className="text-xs text-gray-500">
            Erscheint auf der Detail-Seite
          </p>
          <p className={`text-xs ${(formData.pitch || '').length > 600 ? 'text-amber-600' : 'text-gray-400'}`}>
            {(formData.pitch || '').length}/800 Zeichen
          </p>
        </div>
      </div>

      {/* Description - Optional */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <span className="flex items-center gap-2">
            <AlignLeft className="w-4 h-4 text-gray-600" />
            Weitere Informationen zum GoodPlace
            <span className="text-gray-400 font-normal text-xs">(optional)</span>
          </span>
        </label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => updateFormData({ description: e.target.value })}
          placeholder="Zusätzliche Details, Historie, besondere Angebote, Barrierefreiheit, Insider-Tipps..."
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
        />
        <p className="text-xs text-gray-500 mt-1">
          Alles was nicht in die anderen Felder passt, aber dennoch wissenswert ist
        </p>
      </div>
    </div>
  );
}

export default StepDescription;
